import { Button } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useRecoilState } from "recoil";
import { useFlexForm } from "../../hooks/useFlexForm";
import { FlexFinancialDataPoint, FlexFormStatus } from "../../models/generated/graphql";
import { createBulkFormsValuesState } from "../../store/flexible";
import useUrlQuery from "../../utils/use-url-query";
import { LyfeDialog } from "../dialogs/LyfeDialog";
import NotFound from "../NotFound";
import { FlexWizard } from "./FlexWizard";
import { renderLoadingOrError } from "./utils";

export function FlexRoot(props: {
	typeId?: string;
	entityId?: string;
	isRecordDrawer?: boolean;
	isExistingForm?: boolean;
	getRecordEvaluatedBy?: (data: FlexFinancialDataPoint[] | undefined) => void;
}) {
	const query = useUrlQuery();
	const [bulkPatients, setBulkPatients] = useRecoilState(createBulkFormsValuesState);
	const dynamicParams = Array.from(query.entries()).reduce(
		(accumulator, nextElement) => ({ ...accumulator, [nextElement[0]]: nextElement[1] }),
		{} as any
	);
	const multiplePatients = dynamicParams.multiplePatients ? bulkPatients : [];
	const isNavigatedToEdit = query.has("startEdit");
	const isCopy = query.has("copy");
	const [isEditingSubmitted, setIsEditingSubmitted] = useState(isNavigatedToEdit);
	const { t } = useTranslation();
	const { goBack } = useHistory();
	const {
		loading,
		isFlexEntityMissing,
		flexContext: context,
		pages,
		error,
	} = useFlexForm({
		typeId: props.typeId || null,
		entityId: props.entityId || null,
		dynamicParams: dynamicParams,
		isExistingForm: props.isExistingForm,
		isCopy,
		isEditingSubmitted,
		multiplePatientsCount: multiplePatients.length,
	});

	useEffect(() => {
		props?.getRecordEvaluatedBy &&
			context?.flexEntity?.status !== FlexFormStatus.Unmatched &&
			props.getRecordEvaluatedBy(context?.flexEntity?.financialDataPoints);
	}, [context]);

	useEffect(() => {
		return () => {
			setBulkPatients([]);
		};
	}, []);

	if (props.isExistingForm && isFlexEntityMissing) {
		return <NotFound />;
	}

	if (loading || !!error) {
		return renderLoadingOrError(loading, error)!;
	}

	function handleOkCannotEdit() {
		setIsEditingSubmitted(false);
		if (isNavigatedToEdit) {
			goBack();
		}
	}

	return (
		<>
			<FlexWizard
				key={`${context?.flexEntity?.id}-${context?.isEditable}`}
				context={context!}
				pages={pages!}
				entityId={isCopy ? undefined : props.entityId}
				isRecordDrawer={props.isRecordDrawer}
				handleEditingModeTransition={setIsEditingSubmitted}
				dynamicParams={dynamicParams}
				patients={multiplePatients}
			/>
			<LyfeDialog
				title={t("cannot-proceed")}
				content={[t("no-editable-attributes")]}
				isOpen={context?.areAllAttributesImpossibleToEdit}
			>
				<Button variant="contained" onClick={handleOkCannotEdit} color="primary">
					{t("ok")}
				</Button>
			</LyfeDialog>
		</>
	);
}
